import * as React from 'react';

// react-qr-code (qrcode-generator) is ~20 KB gzip and only the DGA sharing UI renders it, so it loads on demand.
const QRCode = React.lazy(() => import('react-qr-code'));

interface IQrCodeProps extends React.HTMLAttributes<SVGElement> {
  value: string;
  size?: number;
  className?: string;
  /** Called once the QR code SVG is mounted. Never called when the chunk fails to load. */
  onReady?: () => void;
}

/** Keeps a failed chunk import (stale deploy, offline) from taking the sharing UI down: the placeholder stays. */
class QrLoadBoundary extends React.Component<
  { fallback: React.ReactNode; children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export const QrCodeComponent: React.FC<IQrCodeProps> = ({ value, size = 75, className, onReady, ...rest }) => {
  const placeholder = <div className={className} style={{ width: size, height: size }} />;
  const handleRef = React.useCallback(
    (instance: unknown) => {
      if (instance) onReady?.();
    },
    [onReady]
  );

  return (
    <QrLoadBoundary fallback={placeholder}>
      <React.Suspense fallback={placeholder}>
        <QRCode ref={handleRef} value={value} size={size} className={className} {...rest} />
      </React.Suspense>
    </QrLoadBoundary>
  );
};
